import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { VehicleType } from "@api/graphql/generated/generated-types";
import {
  validatePackageDimensions,
  PackageItemDims,
  PackageDimensionAxis,
  PackageMaxDims,
} from "@src/common/utils/packageDimensionsValidation";

interface Args {
  packageItemsFieldName: string;
  vehicleTypeIdFieldName: string;
  vehicleTypes: VehicleType[] | undefined;
}

interface Result {
  hasMaxDimError: boolean;
}

const ALL_AXES: PackageDimensionAxis[] = ["length", "width", "height"];

/**
 * Mirrors the backend `PackageDimensionsValidator` client-side.
 *
 * Watches `packageItems` and `vehicleTypeId`. On any change, recomputes the
 * per-row violations and reflects them as react-hook-form errors at
 * `${packageItemsFieldName}.${index}.${axis}`. The Submit button is then
 * blocked via the returned `hasMaxDimError` flag.
 */
export function usePackageDimensionsValidation({
  packageItemsFieldName,
  vehicleTypeIdFieldName,
  vehicleTypes,
}: Args): Result {
  const { control, setError, clearErrors } = useFormContext();

  const watchedItems = useWatch({
    control,
    name: packageItemsFieldName,
  }) as PackageItemDims[] | undefined;

  const watchedVehicleTypeId = useWatch({
    control,
    name: vehicleTypeIdFieldName,
  }) as string | undefined;

  const selectedVehicle = useMemo(() => {
    if (!watchedVehicleTypeId || !vehicleTypes) return undefined;
    return vehicleTypes.find((vt) => vt.id === watchedVehicleTypeId);
  }, [watchedVehicleTypeId, vehicleTypes]);

  const errors = useMemo(() => {
    const vehicleMaxDims: PackageMaxDims = {
      maxPackageLength: selectedVehicle?.maxPackageLength ?? null,
      maxPackageWidth: selectedVehicle?.maxPackageWidth ?? null,
      maxPackageHeight: selectedVehicle?.maxPackageHeight ?? null,
    };
    return validatePackageDimensions({
      vehicleMaxDims,
      hasVehicleSelected: !!selectedVehicle,
      packageItems: watchedItems ?? null,
    });
  }, [selectedVehicle, watchedItems]);

  useEffect(() => {
    const itemCount = watchedItems?.length ?? 0;

    for (let i = 0; i < itemCount; i++) {
      ALL_AXES.forEach((axis) => {
        clearErrors(`${packageItemsFieldName}.${i}.${axis}`);
      });
    }

    errors.forEach(({ index, axis }) => {
      setError(`${packageItemsFieldName}.${index}.${axis}`, {
        type: "max",
        message: "Too big for vehicle chosen",
      });
    });
  }, [errors, watchedItems?.length, packageItemsFieldName, setError, clearErrors]);

  return { hasMaxDimError: errors.length > 0 };
}
